import { dataSources, getColorScaleDefinition, PLATFORM_RASTER_SIZE } from '../../consts';
import { AreaEmissionsProducts, MethaneLayerIds, Platforms, RASTER_COLORMAP_NAMES, STACCollection } from '../../types';
import { getProductFromCollectionId } from './stacNameTransformations';

/**
 *
 * @param itemID STAC item id
 * @param collectionName STAC collection id
 * @param colormapName optional raster colormap to pass to TiTiler; **default** = `'colormap_name=magma&rescale=0,1000'`
 * @param platform **default** = Platforms.MAIR
 * @returns url string for TiTiler
 */
export const getStacItemCOGUrl = (
    itemID: string,
    collectionName: STACCollection = STACCollection.MethaneAIR_Level4_Portal,
    colormapName: RASTER_COLORMAP_NAMES,
    platform: Platforms = Platforms.MAIR,
    pixelSizeKm2?: number
) => {
    const product = getProductFromCollectionId(collectionName);
    const isConcentrations = product === AreaEmissionsProducts.l3;

    /**
     * rescale: sets min-max inputs for linearly outputting to 0-255 color bins.
     * See https://github.com/developmentseed/titiler/discussions/494#discussioncomment-3113805
     * Do not use for interval color maps (usually used for non-linear color palettes, like the one for MSAT L4 data).
     */
    let scalingParams: string;

    if (isConcentrations) {
        // no rescaling for L3
        scalingParams = '';
    } else if (platform === Platforms.MAIR) {
        const { MIN, MAX } = getColorScaleDefinition(
            platform,
            product,
            MethaneLayerIds.areaEmissionRaster,
            colormapName
        );
        scalingParams = `&rescale=${MIN},${MAX}`;
    } else {
        /**
         * Params for the `normalize` algorithm, which normalizes the resolution of the
         * data before applying a color map to it. Only called with MSAT L4 COG requests
         */
        const algorithmParams = pixelSizeKm2
            ? { pixel_size: pixelSizeKm2 }
            : // back-up in case we aren't passed pixel size - missing from older COGs & their STAC items
              { pixel_size: Math.pow(PLATFORM_RASTER_SIZE[Platforms.MSAT][AreaEmissionsProducts.l4], 2) };

        scalingParams = `&algorithm=normalize&algorithm_params=${encodeURIComponent(JSON.stringify(algorithmParams))}`;
    }

    /**
     * This parameter selects the first band of the COG.
     * COGs produced by the DPP contain 3 bands: mean flux, lower-bound flux, upper-bound flux.
     * We only want to display the mean flux on the map, though the lower & upper bounds will be used
     * to display confidence intervals for raster grid cells.
     * See https://stac-utils.github.io/titiler-pgstac/1.2.3/endpoints/items_endpoints/#tiles
     *
     * For non-DPP COGs, which have only one band, this simply selects the only band available.
     */
    const bandParam = '&asset_bidx=COG|1';

    const urls = getStacDataSource();
    return `${urls.titilerUrl}/collections/${collectionName}/items/${itemID}/tiles/WebMercatorQuad/{z}/{x}/{y}@2x.png?assets=COG&colormap_name=${colormapName}${scalingParams}&nodata=nan${bandParam}`;
};

/**
 * Return urls for emissions data
 * @returns area emissions raster url
 */
export const getStacDataSource = () => {
    return dataSources['area-emission-raster'].url;
};
